'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';

import classNames from 'classnames';
import { chunk, noop } from 'lodash';
import { Mousewheel, Pagination } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

import { Spinner } from '@uikit';

import {
  setAppInCurrentUserSpaceQueryData,
  useCurrentUserOpenableAppListInSpaceQuery,
} from '@query';

import { OpenableApp } from '@api';

import Shop from '@uikit/icons/Shop';

import NextLink from '@shared/common/components/NextLink';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { getErrorMessage } from '@shared/helpers/error';

import { isOpenableApp } from '@store:web/utils/hooks/useAppLink';

import SpacesAppCard from './SpacesAppCard';
import { useRemoveAppFromSpace } from './useRemoveAppFromSpaces';

const SPACE_APP_LIST_LOCAL_STORAGE_KEY = 'apps-in-local-storage';
const SPACE_APP_LIST_LOCAL_STORAGE_VERSION = 1;

interface LocalStorageSpaceAppList {
  version: number;
  data: OpenableApp[];
}

const isLocalStorageSpaceAppList = (value: unknown): value is LocalStorageSpaceAppList => {
  return (
    !!value &&
    typeof value === 'object' &&
    'version' in value &&
    typeof value.version === 'number' &&
    'data' in value &&
    Array.isArray(value.data) &&
    value.data.every(isOpenableApp)
  );
};

const getSpaceAppsInLocalStorage = (): OpenableApp[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const rawAppsInLocalStorage = localStorage?.getItem(SPACE_APP_LIST_LOCAL_STORAGE_KEY);

  if (rawAppsInLocalStorage) {
    try {
      const parsedAppsInLocalStorage = JSON.parse(rawAppsInLocalStorage);

      if (
        isLocalStorageSpaceAppList(parsedAppsInLocalStorage) &&
        parsedAppsInLocalStorage.version === SPACE_APP_LIST_LOCAL_STORAGE_VERSION
      ) {
        return parsedAppsInLocalStorage.data;
      }
    } catch (error) {
      // Do nothing
    }
  }

  return [];
};

export const setSpaceAppsInLocalStorage = (appList: OpenableApp[] = []) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage?.setItem(
    SPACE_APP_LIST_LOCAL_STORAGE_KEY,
    JSON.stringify({
      version: SPACE_APP_LIST_LOCAL_STORAGE_VERSION,
      data: appList,
    }),
  );
};

export const useUserOpenableAppListInSpaceQuery = () => {
  const { isAuth, isLoading: isAuthLoading } = useAuth();
  const queryClient = useQueryClient();
  const query = useCurrentUserOpenableAppListInSpaceQuery({
    enabled: isAuth,
    placeholderData: () => {
      if (isAuth) {
        return [];
      }

      return getSpaceAppsInLocalStorage();
    },
  });

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuth) {
      setAppInCurrentUserSpaceQueryData(queryClient, getSpaceAppsInLocalStorage());
    } else {
      query.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, queryClient, query.refetch, isAuthLoading]);

  return query;
};

interface SpacesAppListTabPanelProps {
  isActive?: boolean;
  onOpen?: (app: OpenableApp) => void;
  onOpenPage?: (app: OpenableApp) => void;
  onRemove?: (app: OpenableApp) => void;
}

const SpacesAppListTabPanel = ({ isActive, onOpen = noop }: SpacesAppListTabPanelProps) => {
  const removeAppFromSpace = useRemoveAppFromSpace();
  const { isLoading: isAuthLoading } = useAuth();
  const { data: appList, isPending: isAppListLoading } = useUserOpenableAppListInSpaceQuery();
  const isLoading = isAuthLoading || isAppListLoading;
  const swiperRef = useRef<SwiperRef>(null);
  const [numberOfAppsPerSlide, setNumberOfAppsPerSlide] = useState(0);
  const appsPerSlide = useMemo(() => {
    if (!numberOfAppsPerSlide) {
      return appList ? [appList] : [];
    }

    return chunk(appList, numberOfAppsPerSlide);
  }, [appList, numberOfAppsPerSlide]);

  const handleRemoveApp = async (app: OpenableApp) => {
    try {
      await removeAppFromSpace(app.id);
    } catch (error) {
      toast.error(`Couldn't remove app from spaces. ${getErrorMessage(error)}. Try again later.`);
    }
  };

  const updateNumberOfAppsPerSlide = useCallback(() => {
    if (!swiperRef.current) {
      return;
    }

    const { swiper } = swiperRef.current;
    const firstSlideEl = swiper.slides[0];

    if (!firstSlideEl) {
      return;
    }

    const slideComputedStyles = window.getComputedStyle(firstSlideEl);
    const gridTemplateColumns = slideComputedStyles.gridTemplateColumns.split(' ');
    const totalColumns = gridTemplateColumns.length;
    const columnSize = parseFloat(gridTemplateColumns[0]);
    const height = parseFloat(slideComputedStyles.height);
    const totalRows = Math.floor(height / columnSize);

    setNumberOfAppsPerSlide(totalRows * totalColumns);
  }, []);

  useLayoutEffect(() => {
    if (swiperRef?.current) {
      swiperRef.current?.swiper?.update();
    }
  }, [isActive, updateNumberOfAppsPerSlide]);

  return (
    <div className={classNames('flex-col h-full', isActive ? 'flex' : 'hidden')}>
      <Spinner size="lg" className={classNames('m-auto', { hidden: !isLoading })} />
      <div className={classNames('flex-1 overflow-hidden', { hidden: isLoading })}>
        <Swiper
          ref={swiperRef}
          slidesPerView={1}
          shortSwipes={false}
          mousewheel
          onAfterInit={updateNumberOfAppsPerSlide}
          onChange={updateNumberOfAppsPerSlide}
          onResize={updateNumberOfAppsPerSlide}
          modules={[Pagination, Mousewheel]}
          threshold={10}
          longSwipesRatio={0.1}
          pagination={{
            el: '.spaces-swiper-pagination',
            clickable: true,
            dynamicBullets: true,
            dynamicMainBullets: 1,
          }}
          className="h-full pt-4"
        >
          {appsPerSlide.map((slideApps, i) => (
            <SwiperSlide
              key={i}
              className="!grid auto-rows-min px-4 grid-cols-[repeat(auto-fill,minmax(120px,1fr))] min-[1600px]:grid-cols-[repeat(auto-fill,minmax(140px,1fr))]"
            >
              {slideApps.map((app) => (
                <SpacesAppCard
                  key={app.id}
                  app={app}
                  onOpen={() => onOpen(app)}
                  onRemove={() => handleRemoveApp(app)}
                />
              ))}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="px-4 my-4 flex items-center h-10 relative">
        <div className="spaces-swiper-pagination" />

        <NextLink
          href="/"
          className={classNames(
            'h-10 right-0 ml-auto rounded-full max-md:hidden flex justify-center items-center t-subtitle2 px-5 gap-2',
            'text-neutral-50 bg-primary shadow-lg shadow-primary/50 hover:shadow-primary/60 hover:bg-primary-500 transition-all',
          )}
          data-qa="spaces-store-button"
        >
          <Shop className="icon-sm" />
          Go To Store
        </NextLink>
      </div>
    </div>
  );
};

export default SpacesAppListTabPanel;
